import { createAsyncThunk, createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { lobbyApi } from '@/services/lobbyApi';
import { ApiClientError } from '@/services/apiClient';
import { lobbyWsClient } from '@/services/lobbyWs';
import type {
  ChatMessage,
  CurrentLobby,
  LobbyEvent,
  LobbyMember,
  LobbyWsState,
  NowPlayingRef,
  PublicLobby,
} from '@/types/lobby';
import { logout } from './authSlice';

const STORAGE_KEY = 'cobstream:lobby';
/**
 * Per-tab persistence for the "Join the watch party?" decline. sessionStorage
 * (not localStorage) is the right scope: a single page reload should NOT
 * re-prompt for a ref the user just declined, but opening a fresh tab is
 * effectively a new lobby session and the user deserves a fresh prompt there.
 */
const DISMISSED_REF_KEY = 'cobstream:lobby:dismissedRef';
const CHAT_MAX_HISTORY = 500;
export const MAX_RECONNECT_ATTEMPTS = 3;
/** ms — base for exponential backoff (1s, 2s, 4s). */
const RECONNECT_BACKOFF_BASE_MS = 1000;

export interface LobbyState {
  currentLobby: CurrentLobby | null;
  members: LobbyMember[];
  chat: ChatMessage[];
  wsState: LobbyWsState;
  /** Last `throttled` warning timestamp — UI flashes "Slow down…" for 2s. */
  throttledAt: number | null;
  /** Set when the lobby ended (`lobby_closed` / `member_left` etc.). */
  closedReason: string | null;
  /** True after MAX_RECONNECT_ATTEMPTS unsuccessful reconnects. */
  disconnected: boolean;
  /** Last error string from a thunk (create/leave/join). */
  error: string | null;
  /** Per-thunk status — keeps the AppNav button accurate during create/leave. */
  status: 'idle' | 'loading' | 'error';
  /** Last server-confirmed `now_playing` ref + source. Drives Watch navigation. */
  nowPlaying: { ref: NowPlayingRef; source: string | null } | null;
  /**
   * Ref the user dismissed the "Join watch party?" prompt for. Cleared when
   * `nowPlaying.ref` changes (so a movie switch re-prompts) or the user leaves
   * the lobby. Compared via `refsEqual()` in the App-level prompt effect.
   */
  dismissedRef: NowPlayingRef | null;
  /**
   * Transient "X joined" / "X left" notice shown as a toast in `App.tsx` so
   * the announcement is visible from every page (not just Watch's chat panel).
   * Reducer rotates `nonce` so a second join by the same user still triggers
   * a fresh render of the toast.
   */
  notice: { kind: 'joined' | 'left'; displayName: string; ts: number; nonce: number } | null;
  /**
   * Last `state` snapshot received over the WS. Cached in the slice so a
   * Watch page that mounts AFTER the snapshot arrived (the common case on a
   * page refresh — App's WS lifecycle runs first, then Watch's player hook)
   * can still snap to the live group position. Cleared on lobby teardown.
   */
  playbackSnapshot: {
    isPlaying: boolean;
    playTimestamp: number;
    lastAnchorAt: number;
    serverNow: number;
    receivedAt: number;
    ref: NowPlayingRef | null;
    source: string | null;
  } | null;
}

/** Structural equality on `NowPlayingRef` discriminated union. */
export function refsEqual(a: NowPlayingRef | null, b: NowPlayingRef | null): boolean {
  if (a === b) return true;
  if (!a || !b) return false;
  if (a.kind !== b.kind) return false;
  if (a.kind === 'movie' && b.kind === 'movie') {
    return a.tmdbId === b.tmdbId;
  }
  if (a.kind === 'tv' && b.kind === 'tv') {
    return a.tmdbId === b.tmdbId && a.season === b.season && a.episode === b.episode;
  }
  if (a.kind === 'anime' && b.kind === 'anime') {
    return a.malId === b.malId && a.episode === b.episode && a.audio === b.audio;
  }
  if (a.kind === 'sports-vod' && b.kind === 'sports-vod') {
    return a.sportsVodId === b.sportsVodId;
  }
  return false;
}

function describeError(err: unknown): string {
  if (err instanceof ApiClientError) return err.message;
  if (err instanceof Error) return err.message;
  return 'Unknown error';
}

// ---------- localStorage helpers ----------

function loadPersisted(): CurrentLobby | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (
      parsed && typeof parsed === 'object'
      && typeof (parsed as CurrentLobby).id === 'string'
      && typeof (parsed as CurrentLobby).code === 'string'
      && typeof (parsed as CurrentLobby).isCreator === 'boolean'
    ) {
      return parsed as CurrentLobby;
    }
    return null;
  } catch {
    return null;
  }
}

function savePersisted(lobby: CurrentLobby | null): void {
  if (typeof window === 'undefined') return;
  try {
    if (lobby) window.localStorage.setItem(STORAGE_KEY, JSON.stringify(lobby));
    else window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // storage quota / disabled — nothing actionable
  }
}

/** Best-effort load of the per-tab dismissed-ref so a reload doesn't re-prompt. */
function loadDismissedRef(): NowPlayingRef | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.sessionStorage.getItem(DISMISSED_REF_KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    const kind = (parsed as { kind?: unknown }).kind;
    if (kind === 'movie' && typeof (parsed as { tmdbId?: unknown }).tmdbId === 'number') {
      return parsed as NowPlayingRef;
    }
    if (kind === 'tv'
      && typeof (parsed as { tmdbId?: unknown }).tmdbId === 'number'
      && typeof (parsed as { season?: unknown }).season === 'number'
      && typeof (parsed as { episode?: unknown }).episode === 'number') {
      return parsed as NowPlayingRef;
    }
    if (kind === 'anime'
      && typeof (parsed as { malId?: unknown }).malId === 'number'
      && typeof (parsed as { episode?: unknown }).episode === 'number'
      && ((parsed as { audio?: unknown }).audio === 'sub' || (parsed as { audio?: unknown }).audio === 'dub')) {
      return parsed as NowPlayingRef;
    }
    if (kind === 'sports-vod'
      && typeof (parsed as { sportsVodId?: unknown }).sportsVodId === 'string') {
      return parsed as NowPlayingRef;
    }
    return null;
  } catch {
    return null;
  }
}

function saveDismissedRef(ref: NowPlayingRef | null): void {
  if (typeof window === 'undefined') return;
  try {
    if (ref) window.sessionStorage.setItem(DISMISSED_REF_KEY, JSON.stringify(ref));
    else window.sessionStorage.removeItem(DISMISSED_REF_KEY);
  } catch {
    // storage quota / disabled — nothing actionable
  }
}

function lobbyToCurrent(lobby: PublicLobby, userId: string): CurrentLobby {
  return {
    id: lobby.id,
    code: lobby.lobbyCode,
    isCreator: lobby.creatorId === userId,
  };
}

// ---------- thunks ----------

export const createMyLobby = createAsyncThunk<
  CurrentLobby,
  void,
  { rejectValue: string; state: { auth: { user: { id: string } | null } } }
>(
  'lobby/create',
  async (_, { getState, rejectWithValue }) => {
    try {
      const userId = getState().auth.user?.id;
      if (!userId) return rejectWithValue('Not authenticated');
      const { lobby } = await lobbyApi.create();
      return lobbyToCurrent(lobby, userId);
    } catch (err) {
      return rejectWithValue(describeError(err));
    }
  },
);

export const joinMyLobby = createAsyncThunk<
  CurrentLobby,
  string,
  { rejectValue: string; state: { auth: { user: { id: string } | null } } }
>(
  'lobby/join',
  async (lobbyCode, { getState, rejectWithValue }) => {
    try {
      const userId = getState().auth.user?.id;
      if (!userId) return rejectWithValue('Not authenticated');
      const trimmed = lobbyCode.trim().toUpperCase();
      if (!trimmed) return rejectWithValue('Lobby code is required');
      const { lobby } = await lobbyApi.join(trimmed);
      return lobbyToCurrent(lobby, userId);
    } catch (err) {
      return rejectWithValue(describeError(err));
    }
  },
);

export const leaveMyLobby = createAsyncThunk<
  void,
  void,
  { rejectValue: string; state: { lobby: LobbyState } }
>(
  'lobby/leave',
  async (_, { getState, rejectWithValue }) => {
    try {
      const current = getState().lobby.currentLobby;
      if (!current) return;
      await lobbyApi.leave(current.id);
    } catch (err) {
      // 404 on leave means the lobby was already gone — treat as success.
      if (err instanceof ApiClientError && err.status === 404) return;
      return rejectWithValue(describeError(err));
    }
  },
);

/**
 * Rehydrate a previously-joined lobby on app boot. We don't have a
 * `/api/lobby/me` endpoint on the backend, so we persist the lobby to
 * localStorage and validate it via `GET /api/lobby/:id` here. If the lobby
 * is gone (404), we clear the persisted state.
 *
 * Note: there's no longer a "still a member" check — the backend dropped
 * the LobbyMember table entirely. Knowledge of the lobby id is the rejoin
 * capability. The 404 path is what handles "lobby ended while I was away."
 */
export const getMyLobby = createAsyncThunk<
  CurrentLobby | null,
  void,
  { rejectValue: string; state: { auth: { user: { id: string } | null } } }
>(
  'lobby/getMine',
  async (_, { getState, rejectWithValue }) => {
    try {
      const userId = getState().auth.user?.id;
      if (!userId) return null;
      const persisted = loadPersisted();
      if (!persisted) return null;
      try {
        const { lobby } = await lobbyApi.getLobbyById(persisted.id);
        return lobbyToCurrent(lobby, userId);
      } catch (err) {
        if (err instanceof ApiClientError && (err.status === 404 || err.status === 403)) {
          savePersisted(null);
          return null;
        }
        throw err;
      }
    } catch (err) {
      return rejectWithValue(describeError(err));
    }
  },
);

// ---------- WS-driven actions ----------

/**
 * Module-scope cleanup for the currently-active `connectToLobbyWs` call.
 * Set by every successful `connectToLobbyWs`; called by:
 *   - the close handler when the socket closes normally,
 *   - a subsequent `connectToLobbyWs` before it installs its own handlers,
 *   - `disconnectFromLobbyWs` when the consumer wants to tear down explicitly.
 *
 * Without this, the unsubscribers from a prior thunk invocation stayed
 * attached to the shared `lobbyWsClient` handler sets when the consumer
 * called `lobbyWsClient.disconnect()` directly (i.e. an "external"
 * teardown that didn't go through the close path). The stale handlers
 * then fired on the next connection's open/close, double-dispatching
 * `wsOpened` / `wsClosed` and visibly flickering the wsState label on
 * leave+rejoin.
 */
let activeWsCleanup: (() => void) | null = null;

/**
 * Connect to the lobby WS for the currently-set lobby. The thunk wires WS
 * lifecycle events to slice actions; the caller should dispatch
 * `disconnectFromLobbyWs` on teardown so subscribers are removed even when
 * the socket is still open.
 */
export const connectToLobbyWs = createAsyncThunk<
  void,
  { lobbyId: string; token: string; client?: typeof lobbyWsClient },
  { state: { lobby: LobbyState } }
>(
  'lobby/connectWs',
  async ({ lobbyId, token, client }, { dispatch }) => {
    const wsClient = client ?? lobbyWsClient;
    // Tear down any prior subscribers BEFORE we install new ones — otherwise
    // they'd both fire on this connection's events.
    if (activeWsCleanup) {
      activeWsCleanup();
      activeWsCleanup = null;
    }
    wsClient.disconnect(1000, 'reconnecting');

    const unsubscribers: Array<() => void> = [];
    const cleanup = (): void => {
      while (unsubscribers.length > 0) {
        const u = unsubscribers.pop();
        try { u?.(); } catch { /* handler unsub shouldn't throw */ }
      }
      // Only clear if we're still the active cleanup — guards against a
      // newer connect having replaced us between scheduling and execution.
      if (activeWsCleanup === cleanup) activeWsCleanup = null;
    };

    unsubscribers.push(wsClient.onOpen(() => {
      dispatch(lobbySlice.actions.wsOpened());
    }));
    unsubscribers.push(wsClient.onClose(({ code, reason }) => {
      dispatch(lobbySlice.actions.wsClosed({ code, reason }));
      cleanup();
    }));
    unsubscribers.push(wsClient.on('*', (event) => {
      dispatch(lobbySlice.actions.wsEventReceived(event));
    }));

    activeWsCleanup = cleanup;
    dispatch(lobbySlice.actions.wsConnecting());
    wsClient.connect(lobbyId, token);
  },
);

/**
 * Tear down the active WS subscriptions and close the socket. Safe to call
 * when there is no active connection — both the cleanup and `disconnect`
 * are no-ops in that case.
 */
export const disconnectFromLobbyWs = createAsyncThunk<
  void,
  { client?: typeof lobbyWsClient } | undefined,
  { state: { lobby: LobbyState } }
>(
  'lobby/disconnectWs',
  async (arg) => {
    const wsClient = arg?.client ?? lobbyWsClient;
    if (activeWsCleanup) {
      activeWsCleanup();
      activeWsCleanup = null;
    }
    wsClient.disconnect(1000, 'leave');
  },
);

/** Test-only — reset the module-scope cleanup ref between cases. */
export function __resetLobbyWsCleanup(): void {
  if (activeWsCleanup) {
    try { activeWsCleanup(); } catch { /* ignore */ }
  }
  activeWsCleanup = null;
}

/**
 * Send a chat message via the singleton WS client. Resolves to true if the
 * send was actually attempted (socket open), false if dropped.
 */
export const sendChatMessage = createAsyncThunk<
  boolean,
  { text: string; client?: typeof lobbyWsClient },
  { state: { lobby: LobbyState } }
>(
  'lobby/sendChat',
  async ({ text, client }, { getState }) => {
    const wsClient = client ?? lobbyWsClient;
    const trimmed = text.trim();
    if (!trimmed) return false;
    if (getState().lobby.wsState !== 'open') return false;
    wsClient.sendChat(trimmed.slice(0, 256));
    return true;
  },
);

// ---------- slice ----------

const initialState: LobbyState = {
  currentLobby: null,
  members: [],
  chat: [],
  wsState: 'idle',
  throttledAt: null,
  closedReason: null,
  disconnected: false,
  error: null,
  status: 'idle',
  nowPlaying: null,
  // Hydrated from sessionStorage so a reload after declining the prompt
  // doesn't immediately re-prompt for the same ref.
  dismissedRef: loadDismissedRef(),
  notice: null,
  playbackSnapshot: null,
};

const lobbySlice = createSlice({
  name: 'lobby',
  initialState,
  reducers: {
    /** Manually clear all lobby state (used after a "lobby ended" notice is dismissed). */
    clearLobby(state) {
      state.currentLobby = null;
      state.members = [];
      state.chat = [];
      state.wsState = 'idle';
      state.throttledAt = null;
      state.closedReason = null;
      state.disconnected = false;
      state.error = null;
      state.status = 'idle';
      state.nowPlaying = null;
      state.dismissedRef = null;
      state.notice = null;
      state.playbackSnapshot = null;
      savePersisted(null);
      saveDismissedRef(null);
    },
    /**
     * App-level toast dismissed (auto-fade or click). Clears the transient
     * notice so it doesn't re-render on the next unrelated state change.
     */
    dismissNotice(state) {
      state.notice = null;
    },
    /**
     * User clicked "Not now" on the Join Watch Party prompt for the current
     * `nowPlaying.ref`. The App-level effect suppresses the prompt while
     * `nowPlaying.ref` still matches; a ref change re-prompts. Persisted to
     * sessionStorage so a reload doesn't immediately re-show the modal.
     */
    dismissJoinPrompt(state, action: PayloadAction<NowPlayingRef>) {
      state.dismissedRef = action.payload;
      saveDismissedRef(action.payload);
    },
    wsConnecting(state) {
      state.wsState = 'connecting';
      state.disconnected = false;
    },
    wsOpened(state) {
      state.wsState = 'open';
      state.disconnected = false;
    },
    wsClosed(state, action: PayloadAction<{ code: number; reason: string }>) {
      state.wsState = 'closed';
      // If the lobby itself was torn down server-side, surface that to the UI
      // and clear the current lobby so the AppNav button flips back to "Create".
      const reason = action.payload.reason;
      if (reason === 'lobby_closed' || reason === 'stale' || reason === 'creator_left' || reason === 'creator_deleted') {
        // The `lobby_closed` event handler below runs FIRST (the event is
        // sent on the wire just before the socket is closed) and already
        // wrote a more specific message based on the event's `reason` field.
        // Don't clobber it with the generic fallback derived from the WS
        // close-reason string.
        if (!state.closedReason) {
          state.closedReason = reason === 'creator_left' ? 'Lobby ended (host left)'
            : reason === 'stale' ? 'Lobby ended (inactive for too long)'
            : 'Lobby ended';
        }
        state.currentLobby = null;
        state.members = [];
        state.nowPlaying = null;
        state.dismissedRef = null;
        state.notice = null;
        state.playbackSnapshot = null;
        savePersisted(null);
        saveDismissedRef(null);
      }
    },
    /**
     * Marked disconnected after MAX_RECONNECT_ATTEMPTS failures. Watch.tsx
     * uses this to stop attempting reconnects + surface the "disconnected"
     * banner.
     */
    wsGaveUp(state) {
      state.disconnected = true;
      state.wsState = 'closed';
    },
    wsEventReceived(state, action: PayloadAction<LobbyEvent>) {
      const event = action.payload;
      switch (event.type) {
        case 'state': {
          // Preserve any displayName captured from prior `joined`/`chat` events.
          // The state event only carries memberIds, so rebuilding members from
          // it alone would wipe the names — manifesting as system messages and
          // chat labels reverting to raw userIds after a reconnect or initial
          // state snapshot.
          const prevNames = new Map(state.members.map((m) => [m.userId, m.displayName]));
          state.members = event.memberIds.map((userId) => {
            const displayName = prevNames.get(userId);
            return displayName ? { userId, displayName } : { userId };
          });
          state.nowPlaying = event.ref ? { ref: event.ref, source: event.source } : null;
          // Cache the snapshot so a Watch page mounted AFTER the state event
          // arrived (e.g. on a full-page refresh) can still snap to the live
          // group position. useLobbyPlayerSync reads this on MANIFEST_PARSED.
          state.playbackSnapshot = {
            isPlaying: event.isPlaying,
            playTimestamp: event.playTimestamp,
            lastAnchorAt: event.lastAnchorAt,
            serverNow: typeof event.serverNow === 'number' ? event.serverNow : Date.now(),
            receivedAt: Date.now(),
            ref: event.ref,
            source: event.source,
          };
          break;
        }
        case 'joined': {
          const exists = state.members.some((m) => m.userId === event.userId);
          if (!exists) {
            state.members.push({ userId: event.userId, displayName: event.displayName });
          } else if (event.displayName) {
            state.members = state.members.map((m) => (
              m.userId === event.userId ? { ...m, displayName: event.displayName } : m
            ));
          }
          state.chat.push({
            from: '__system__',
            displayName: 'system',
            text: `${event.displayName ?? event.userId} joined`,
            ts: Date.now(),
            kind: 'system',
          });
          // Toast notice is shown app-level (visible from any route).
          state.notice = {
            kind: 'joined',
            displayName: event.displayName ?? event.userId,
            ts: Date.now(),
            nonce: (state.notice?.nonce ?? 0) + 1,
          };
          break;
        }
        case 'disconnected':
        case 'member_left': {
          const label = state.members.find((m) => m.userId === event.userId)?.displayName ?? event.userId;
          state.members = state.members.filter((m) => m.userId !== event.userId);
          state.chat.push({
            from: '__system__',
            displayName: 'system',
            text: event.type === 'member_left' ? `${label} left` : `${label} disconnected`,
            ts: Date.now(),
            kind: 'system',
          });
          state.notice = {
            kind: 'left',
            displayName: label,
            ts: Date.now(),
            nonce: (state.notice?.nonce ?? 0) + 1,
          };
          break;
        }
        case 'chat': {
          state.chat.push({
            from: event.userId,
            displayName: event.displayName,
            text: event.text,
            ts: event.ts,
            kind: 'chat',
          });
          break;
        }
        case 'throttled': {
          state.throttledAt = Date.now();
          break;
        }
        case 'lobby_closed': {
          state.closedReason = event.reason === 'creator_left' ? 'Lobby ended (host left)'
            : event.reason === 'creator_deleted' ? 'Lobby was closed by the host'
            : 'Lobby ended (inactive)';
          state.currentLobby = null;
          state.members = [];
          state.wsState = 'closed';
          state.nowPlaying = null;
          state.dismissedRef = null;
          state.notice = null;
          state.playbackSnapshot = null;
          savePersisted(null);
          saveDismissedRef(null);
          break;
        }
        case 'error': {
          // Surface server-side command errors as non-blocking system chat.
          state.chat.push({
            from: '__system__',
            displayName: 'system',
            text: `Error: ${event.message}`,
            ts: Date.now(),
            kind: 'system',
          });
          break;
        }
        case 'now_playing': {
          state.nowPlaying = { ref: event.ref, source: event.source };
          break;
        }
        default:
          // play / pause / seek / resync — handled by useLobbyPlayerSync, not Redux.
          break;
      }
      // Cap chat history so we don't grow forever.
      if (state.chat.length > CHAT_MAX_HISTORY) {
        state.chat = state.chat.slice(state.chat.length - CHAT_MAX_HISTORY);
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // Logout drops all lobby state.
      .addCase(logout, (state) => {
        state.currentLobby = null;
        state.members = [];
        state.chat = [];
        state.wsState = 'idle';
        state.throttledAt = null;
        state.closedReason = null;
        state.disconnected = false;
        state.error = null;
        state.status = 'idle';
        state.nowPlaying = null;
        state.dismissedRef = null;
        state.notice = null;
        state.playbackSnapshot = null;
        savePersisted(null);
        saveDismissedRef(null);
      })

      .addCase(createMyLobby.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(createMyLobby.fulfilled, (state, action) => {
        state.status = 'idle';
        state.currentLobby = action.payload;
        state.closedReason = null;
        state.disconnected = false;
        savePersisted(action.payload);
      })
      .addCase(createMyLobby.rejected, (state, action) => {
        state.status = 'error';
        state.error = action.payload ?? action.error.message ?? 'Failed to create lobby';
      })

      .addCase(joinMyLobby.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(joinMyLobby.fulfilled, (state, action) => {
        state.status = 'idle';
        state.currentLobby = action.payload;
        state.closedReason = null;
        state.disconnected = false;
        savePersisted(action.payload);
      })
      .addCase(joinMyLobby.rejected, (state, action) => {
        state.status = 'error';
        state.error = action.payload ?? action.error.message ?? 'Failed to join lobby';
      })

      .addCase(leaveMyLobby.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(leaveMyLobby.fulfilled, (state) => {
        state.status = 'idle';
        state.currentLobby = null;
        state.members = [];
        state.chat = [];
        state.wsState = 'idle';
        state.closedReason = null;
        state.disconnected = false;
        state.nowPlaying = null;
        state.dismissedRef = null;
        state.notice = null;
        state.playbackSnapshot = null;
        savePersisted(null);
        saveDismissedRef(null);
      })
      .addCase(leaveMyLobby.rejected, (state, action) => {
        state.status = 'error';
        state.error = action.payload ?? action.error.message ?? 'Failed to leave lobby';
      })

      .addCase(getMyLobby.fulfilled, (state, action) => {
        if (action.payload) {
          state.currentLobby = action.payload;
          savePersisted(action.payload);
        } else {
          // Clear out any stale persisted state.
          state.currentLobby = null;
          savePersisted(null);
        }
      });
  },
});

export const {
  clearLobby,
  wsConnecting,
  wsOpened,
  wsClosed,
  wsGaveUp,
  wsEventReceived,
  dismissJoinPrompt,
  dismissNotice,
} = lobbySlice.actions;

export const RECONNECT = {
  MAX_ATTEMPTS: MAX_RECONNECT_ATTEMPTS,
  BACKOFF_BASE_MS: RECONNECT_BACKOFF_BASE_MS,
} as const;

export default lobbySlice.reducer;
